import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import {
  defaultEmployeeStatusOptions,
  defaultRegistrationStatus,
} from '@nxsys/constants/AppConstants';
import { AppState } from '@nxsys/store';
import { getAppConfig } from '@nxsys/utils';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { hasFilterValue } from '@nxsys/utils/filterForm';

type FilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: FilterValues) => void;
};

export type FilterValues = {
  paymentModel: string;
  registrationStatus: string;
  companyIds: string[];
  agencyIds: string[];
  isLeft: boolean | null;
  isNotOnboarding: boolean | undefined;
  isOnHold: boolean | undefined;
};

const FilterModal: React.FC<FilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
}: FilterModalProps) => {
  const { messages } = useTypedIntl();
  const { isBureau, isBureauEmployer } = useSelector(
    (state: AppState) => state.appStore,
  );
  const defaultPaymentModel = getAppConfig('paymentModels');

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  const filterOptions = useMemo(() => {
    if (isBureau) {
      return [
        {
          label: messages['common.employeeStatus'],
          name: 'employeeStatus',
          filterType: 'select',
          options: [
            {
              value: 'NOT_ONBOARDING',
              label: 'Not Send Onboarding yet',
            },
            ...defaultEmployeeStatusOptions,
          ],
          placeholder: messages['common.selectEmployeeStatus'],
        },
        {
          label: messages['common.employer'],
          name: 'companyList',
          filterType: 'selectSync',
          selectSyncType: 'company',
          apiType: 'searchCompany',
          placeholder: messages['form.selectEmployer'],
        },
        {
          label: messages['table.registrationStatus'],
          name: 'registrationStatus',
          filterType: 'select',
          options: defaultRegistrationStatus,
          placeholder: messages['common.selectRegistrationStatus'],
        },
        {
          label: 'On Hold',
          name: 'onHold',
          filterType: 'select',
          options: [
            { value: 'YES', label: 'Yes' },
            { value: 'NO', label: 'No' },
          ],
          placeholder: 'Select On Hold',
        },
      ];
    }
    if (isBureauEmployer) {
      return [
        {
          label: messages['common.employeeStatus'],
          name: 'registrationStatus',
          filterType: 'select',
          options: defaultEmployeeStatusOptions,
          placeholder: messages['common.selectEmployeeStatus'],
        },
        {
          label: messages['table.registrationStatus'],
          name: 'registrationStatus',
          filterType: 'select',
          options: defaultRegistrationStatus,
          placeholder: messages['common.selectRegistrationStatus'],
        },
        {
          label: 'On Hold',
          name: 'onHold',
          filterType: 'select',
          options: [
            { value: 'YES', label: 'Yes' },
            { value: 'NO', label: 'No' },
          ],
          placeholder: 'Select On Hold',
        },
      ];
    }
    return [
      {
        label: messages['common.employeeStatus'],
        name: 'employeeStatus',
        filterType: 'select',
        options: defaultEmployeeStatusOptions,
        placeholder: messages['common.selectEmployeeStatus'],
      },
      {
        label: messages['common.paymentCompany'],
        name: 'companyList',
        filterType: 'selectSync',
        selectSyncType: 'company',
        apiType: 'searchCompany',
        placeholder: messages['common.selectPaymentCompany'],
      },
      {
        label: messages['common.agency'],
        name: 'agencyList',
        filterType: 'selectSync',
        selectSyncType: 'agency',
        apiType: 'searchAgency',
        placeholder: messages['common.selectAgency'],
      },
      {
        label: messages['table.registrationStatus'],
        name: 'registrationStatus',
        filterType: 'select',
        options: defaultRegistrationStatus,
        placeholder: messages['common.selectRegistrationStatus'],
      },
      {
        label: messages['table.paymentModel'],
        name: 'paymentModel',
        filterType: 'select',
        options: defaultPaymentModel,
        placeholder: messages['common.selectPaymentModel'],
      },
      {
        label: 'On Hold',
        name: 'onHold',
        filterType: 'select',
        options: [
          { value: 'YES', label: 'Yes' },
          { value: 'NO', label: 'No' },
        ],
        placeholder: 'Select On Hold',
      },
    ];
  }, [messages, isBureau, isBureauEmployer, defaultPaymentModel]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params = {
      paymentModel: values?.paymentModel,
      registrationStatus: values?.registrationStatus,
      companyIds: values?.companyList?.map((c: any) => c.value),
      agencyIds: values?.agencyList?.map((a: any) => a.value),
      isLeft:
        values?.employeeStatus === 'ACTIVE'
          ? false
          : values?.employeeStatus === 'LEAVER'
            ? true
            : null,
      isNotOnboarding:
        values?.employeeStatus === 'NOT_ONBOARDING' ? true : undefined,
      isOnHold:
        values?.onHold === 'YES'
          ? true
          : values?.onHold === 'NO'
            ? false
            : undefined,
    };
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {filterOptions?.map((f) => {
          return (
            <Form.Item
              key={f.name}
              label={f.label}
              name={f.name}
              layout="vertical"
            >
              {f.filterType === 'select' && (
                <AppSelect
                  placeholder={f.placeholder}
                  options={f.options ?? []}
                  onChange={(value) => {
                    form.setFieldsValue({
                      [f.name]: value.value,
                    });
                  }}
                />
              )}
              {f.filterType === 'selectSync' && (
                <AppSelectSync
                  type={f.selectSyncType ?? ''}
                  apiType={f.apiType as any}
                  placeholder={f.placeholder}
                  onChangeValue={(value: OptionType | OptionType[] | null) => {
                    form.setFieldsValue({
                      [f.name]: value,
                    });
                  }}
                  mode={'multiple'}
                />
              )}
            </Form.Item>
          );
        })}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
